import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import ImageWithFallback from "../components/ImageWithFallback";
import { Sparkle, BookmarkPlus, Share2, ArrowLeft, Plane, Check, ExternalLink } from "lucide-react";

function fmtRange(w) {
    if (!w) return "";
    const d1 = new Date(w.start);
    const d2 = new Date(w.end);
    const opt = { month: "long", day: "numeric", year: "numeric" };
    return `${d1.toLocaleDateString("en-US", opt)} – ${d2.toLocaleDateString("en-US", opt)}`;
}

export default function Recommendation() {
    const { id } = useParams();
    const [sp] = useSearchParams();
    const season = sp.get("season") || "this";
    const [rec, setRec] = useState(null);
    const [error, setError] = useState("");
    const [saved, setSaved] = useState(false);
    const [busy, setBusy] = useState(false);
    const [share, setShare] = useState(false);

    useEffect(() => {
        api.get(`/recommendations/${id}?season=${season}`)
            .then((r) => setRec(r.data))
            .catch((e) => setError(e.response?.data?.detail || e.message));
    }, [id, season]);

    const handleSave = async () => {
        if (saved || busy) return;
        setBusy(true);
        try {
            await api.post("/trips/save", { destination_id: id, season });
            setSaved(true);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    const recordShare = () => {
        api.post("/events", { event_type: "share", destination_id: id }).catch(() => {});
    };

    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    const shareText = rec ? `StarPath says ${rec.city} (${rec.planet} season) is calling me ✨` : "";

    if (error) {
        return (
            <Layout>
                <div className="sp-container py-24 text-center">
                    <div className="sp-overline">Something went wrong</div>
                    <p className="mt-4 text-ink-soft" data-testid="recommendation-error">{error}</p>
                    <Link to="/dashboard" className="sp-btn-ghost mt-8 inline-flex">Back to dashboard</Link>
                </div>
            </Layout>
        );
    }
    if (!rec) {
        return <Layout><div className="sp-container py-24 sp-overline" data-testid="recommendation-loading">Reading the sky…</div></Layout>;
    }

    return (
        <Layout>
            <div className="relative w-full overflow-hidden" style={{ maxHeight: "60vh" }}>
                <ImageWithFallback
                    src={rec.image}
                    alt={rec.city}
                    accentColor={rec.planet_color}
                    imgClassName="w-full h-[60vh] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-sand-50 via-sand-50/20 to-transparent" />
                {rec.image_photographer && (
                    <div
                        className="absolute top-4 right-4 z-10 bg-black/40 backdrop-blur-md text-white/90 text-[11px] tracking-wide px-3 py-1.5 rounded-full border border-white/10"
                        data-testid="recommendation-photo-credit"
                    >
                        Photo by{" "}
                        <a
                            href={rec.image_photographer_url || "https://www.pexels.com"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2 hover:text-white"
                            data-testid="recommendation-photo-credit-photographer"
                        >
                            {rec.image_photographer}
                        </a>
                        {" on "}
                        <a
                            href={rec.image_pexels_url || "https://www.pexels.com"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2 hover:text-white"
                            data-testid="recommendation-photo-credit-pexels"
                        >
                            Pexels
                        </a>
                    </div>
                )}
                <div className="sp-container absolute inset-x-0 bottom-8">
                    <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="recommendation-back">
                        <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Back
                    </Link>
                    <div className="sp-overline mt-4 text-ink">{rec.planet_theme} · {rec.season}</div>
                    <h1 className="text-4xl sm:text-5xl lg:text-7xl font-serif mt-2 leading-[1.02]" data-testid="recommendation-title">
                        {rec.city}<span className="text-ink-muted">, {rec.country}</span>
                    </h1>
                </div>
            </div>

            <div className="sp-container pt-12 pb-24 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-8 space-y-12">
                    <section>
                        <div className="sp-overline">Why now</div>
                        <p className="mt-4 text-lg sm:text-xl text-ink-soft font-serif leading-relaxed" data-testid="recommendation-explanation">
                            {rec.explanation}
                        </p>
                    </section>

                    <section>
                        <div className="sp-overline">Suggested experiences</div>
                        <ul className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="recommendation-experiences">
                            {rec.experiences.map((exp) => (
                                <li key={exp} className="flex items-start gap-3 bg-surface border border-ink/10 rounded-2xl p-4">
                                    <Sparkle strokeWidth={1.25} className="w-4 h-4 mt-0.5" style={{ color: rec.planet_color }} />
                                    <span className="text-sm leading-relaxed">{exp}</span>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section>
                        <div className="sp-overline">A note on the planet</div>
                        <p className="mt-4 text-ink-soft leading-relaxed">{rec.planet_blurb}</p>
                    </section>
                </div>

                <aside className="lg:col-span-4">
                    <div className="lg:sticky lg:top-28 space-y-4">
                        <div className="bg-surface border border-ink/10 rounded-2xl p-6">
                            <div className="sp-overline">Best travel dates</div>
                            <div className="font-serif text-xl mt-2" data-testid="recommendation-window">{fmtRange(rec.window)}</div>
                            {rec.window?.duration_days !== undefined && (
                                <div className="text-xs text-ink-muted mt-1" data-testid="recommendation-duration">
                                    {rec.window.duration_days}-day trip · curated for the {rec.planet} rhythm
                                </div>
                            )}

                            {rec.window?.confidence_score !== undefined && (
                                <div className="mt-5" data-testid="recommendation-confidence-block">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-ink-muted">Astrological confidence</span>
                                        <span className="font-serif text-2xl" data-testid="recommendation-confidence">
                                            {rec.window.confidence_score}%
                                        </span>
                                    </div>
                                    <div className="h-1 mt-2 bg-sand-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full"
                                            style={{
                                                width: `${rec.window.confidence_score}%`,
                                                background: rec.planet_color,
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {rec.window?.astrological_reason && (
                                <div className="mt-5 pt-5 border-t border-ink/10" data-testid="recommendation-window-reason">
                                    <div className="sp-overline">Why these dates</div>
                                    <p className="mt-2 text-sm text-ink-soft leading-relaxed font-serif italic">
                                        {rec.window.astrological_reason}
                                    </p>
                                </div>
                            )}

                            <div className="mt-6 space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-ink-muted">Match score</span>
                                    <span className="font-serif text-2xl" data-testid="recommendation-score">{rec.score}/100</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-ink-muted">Dominant planet</span>
                                    <span style={{ color: rec.planet_color }}>{rec.planet}</span>
                                </div>
                            </div>

                            <div className="mt-6 flex gap-2">
                                <button onClick={handleSave} disabled={saved || busy} className="sp-btn-primary flex-1" data-testid="recommendation-save">
                                    {saved ? (<><Check strokeWidth={1.5} className="w-4 h-4" /> Saved</>) : (<><BookmarkPlus strokeWidth={1.5} className="w-4 h-4" /> Save trip</>)}
                                </button>
                                <button onClick={() => setShare((s) => !s)} className="sp-btn-ghost" data-testid="recommendation-share-toggle" aria-label="Share">
                                    <Share2 strokeWidth={1.5} className="w-4 h-4" />
                                </button>
                            </div>

                            {share && (
                                <div className="mt-4 pt-4 border-t border-ink/10 space-y-2" data-testid="recommendation-share-menu">
                                    <a className="block text-sm sp-link" data-testid="share-sms" href={`sms:?body=${encodeURIComponent(shareText + " " + shareUrl)}`} onClick={recordShare}>Text it to someone</a>
                                    <a className="block text-sm sp-link" data-testid="share-email" href={`mailto:?subject=${encodeURIComponent("StarPath recommendation")}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`} onClick={recordShare}>Email it</a>
                                    <a className="block text-sm sp-link" data-testid="share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`} target="_blank" rel="noreferrer" onClick={recordShare}>WhatsApp</a>
                                    <a className="block text-sm sp-link" data-testid="share-x" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" onClick={recordShare}>X (Twitter)</a>
                                    <a className="block text-sm sp-link" data-testid="share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" onClick={recordShare}>Facebook</a>
                                    <button
                                        className="block text-sm sp-link text-left"
                                        data-testid="share-copy"
                                        onClick={() => { navigator.clipboard?.writeText(shareUrl); recordShare(); }}
                                    >
                                        Copy link
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="bg-surface border border-ink/10 rounded-2xl p-6">
                            <div className="sp-overline flex items-center gap-2">
                                <Plane strokeWidth={1.5} className="w-3.5 h-3.5" /> Flights
                                <span className="ml-auto text-[10px] text-ink-muted tracking-wider uppercase" data-testid="recommendation-flights-source">Live · TravelPayouts</span>
                            </div>
                            {rec.flight_origin && (
                                <div className="text-xs text-ink-muted mt-2">From your nearest airport: <span className="font-mono text-ink">{rec.flight_origin}</span></div>
                            )}
                            {rec.flights?.length === 0 && (
                                <div className="mt-4" data-testid="recommendation-flights-empty">
                                    <p className="text-sm text-ink-soft">
                                        {rec.flights_error || "No cached fares for this exact window."} Search live availability on Aviasales:
                                    </p>
                                    {rec.flights_search_url && (
                                        <a
                                            href={rec.flights_search_url}
                                            target="_blank"
                                            rel="noopener noreferrer sponsored"
                                            onClick={() => api.post("/events", { event_type: "flight_search_click", destination_id: id }).catch(() => {})}
                                            className="mt-3 inline-flex items-center gap-1.5 sp-btn-primary text-xs px-4 py-2"
                                            data-testid="recommendation-flights-search-link"
                                        >
                                            Search on Aviasales
                                            <ExternalLink strokeWidth={1.5} className="w-3 h-3" />
                                        </a>
                                    )}
                                </div>
                            )}
                            <ul className="mt-4 space-y-3" data-testid="recommendation-flights">
                                {rec.flights?.map((f, i) => (
                                    <li key={f.flight_number || `${f.airline}-${i}`} className="border border-ink/10 rounded-xl p-4 hover:border-ink/30 transition-colors" data-testid={`recommendation-flight-${i}`}>
                                        <div className="flex items-center justify-between">
                                            <span className="font-mono text-xs text-ink-soft">{f.airline}{f.flight_number ? ` · ${f.flight_number}` : ""}</span>
                                            <span className="font-serif text-lg">{f.currency === "USD" ? "$" : ""}{f.price}{f.currency !== "USD" ? ` ${f.currency}` : ""}</span>
                                        </div>
                                        <div className="font-mono text-sm mt-1">
                                            {f.departure_airport} → {f.arrival_airport}
                                        </div>
                                        <div className="text-xs text-ink-muted mt-1">
                                            {f.duration && <>{f.duration} · </>}
                                            {(f.stops === 0 || f.stops === undefined) ? "Nonstop" : `${f.stops} stop${f.stops > 1 ? "s" : ""}`}
                                            {f.departure_date && <> · departs {new Date(f.departure_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</>}
                                        </div>
                                        {f.booking_url && (
                                            <a
                                                href={f.booking_url}
                                                target="_blank"
                                                rel="noopener noreferrer sponsored"
                                                onClick={() => api.post("/events", { event_type: "flight_booking_click", destination_id: id, metadata: { airline: f.airline, price: f.price, currency: f.currency } }).catch(() => {})}
                                                className="mt-3 inline-flex items-center gap-1.5 sp-btn-primary text-xs px-4 py-2"
                                                data-testid={`recommendation-flight-${i}-book`}
                                            >
                                                Book flight
                                                <ExternalLink strokeWidth={1.5} className="w-3 h-3" />
                                            </a>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </aside>
            </div>
        </Layout>
    );
}
